<template>
  <div class="p-4 md:p-6">
    <!-- Header -->
    <div class="flex items-start justify-between gap-3 mb-6 md:mb-8">
      <div class="min-w-0">
        <h1 class="text-xl md:text-2xl font-bold text-gray-900">אנליטיקות עסקיות</h1>
        <p class="text-xs md:text-sm text-gray-400 mt-1">המרת ניסיון לתשלום ושימור לקוחות לפי חודש הרשמה (ללא עסקי בדיקה)</p>
      </div>
      <div class="flex gap-2 flex-shrink-0 items-center">
        <Select
          v-model="selectedMonths"
          :options="monthsOptions"
          optionLabel="label"
          optionValue="value"
          class="!text-sm"
        />
        <Button
          icon="pi pi-refresh"
          size="small"
          text
          rounded
          :loading="loading"
          class="!text-gray-400 hover:!text-gray-900 hover:!bg-gray-100"
          @click="fetchData"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading && !funnel" class="flex justify-center items-center h-64">
      <ProgressSpinner style="width: 50px; height: 50px" />
    </div>

    <template v-if="funnel">
      <!-- Totals KPIs -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="הרשמות בתקופה" :value="funnel.totals.signups" icon="pi-plus-circle" color="blue" />
        <KpiCard label="משלמים מתוכן" :value="funnel.totals.paying" icon="pi-verified" color="green" />
        <KpiCard label="אחוז המרה" :value="`${funnel.totals.conversionRate}%`" icon="pi-percentage" color="purple" />
        <KpiCard label="MRR מהתקופה" :value="formatCurrency(funnel.totals.mrr)" icon="pi-wallet" color="amber" />
      </div>

      <!-- Funnel chart -->
      <div class="bg-white border border-gray-200 rounded-2xl p-4 md:p-5 mb-6 shadow-sm">
        <h2 class="text-sm font-semibold text-gray-900 mb-3">משפך המרה לפי חודש הרשמה — איפה כל קוהורטה עומדת היום</h2>
        <Chart type="bar" :data="funnelChartData" :options="funnelChartOptions" class="h-72" />
      </div>

      <!-- Funnel table -->
      <div class="bg-white border border-gray-200 rounded-2xl overflow-x-auto shadow-sm mb-6">
        <DataTable :value="funnel.months" class="!text-sm" stripedRows>
          <Column field="month" header="חודש הרשמה" style="min-width: 110px">
            <template #body="{ data }">
              <span class="font-medium text-gray-900">{{ formatMonth(data.month) }}</span>
            </template>
          </Column>
          <Column field="signups" header="הרשמות" style="min-width: 80px" />
          <Column field="paying" header="משלמים" style="min-width: 80px">
            <template #body="{ data }">
              <span class="font-semibold text-emerald-600">{{ data.paying }}</span>
            </template>
          </Column>
          <Column field="trial" header="בניסיון" style="min-width: 80px" />
          <Column field="free" header="חינמי" style="min-width: 80px" />
          <Column field="expired" header="פג תוקף" style="min-width: 80px">
            <template #body="{ data }">
              <span :class="data.expired > 0 ? 'text-red-500 font-medium' : 'text-gray-300'">{{ data.expired }}</span>
            </template>
          </Column>
          <Column field="conversionRate" header="המרה" style="min-width: 80px">
            <template #body="{ data }">
              <Tag
                :value="`${data.conversionRate}%`"
                :severity="data.conversionRate >= 20 ? 'success' : data.conversionRate > 0 ? 'warn' : 'secondary'"
              />
            </template>
          </Column>
          <Column field="mrr" header="MRR" style="min-width: 90px">
            <template #body="{ data }">
              <span v-if="data.mrr" class="font-medium">{{ formatCurrency(data.mrr) }}</span>
              <span v-else class="text-gray-300">—</span>
            </template>
          </Column>
          <Column header="שלבי CRM" style="min-width: 220px">
            <template #body="{ data }">
              <div class="flex flex-wrap gap-1">
                <Tag
                  v-for="(count, stage) of data.leadStages"
                  :key="stage"
                  :value="`${stageLabel(String(stage))}: ${count}`"
                  :severity="stageSeverity(String(stage))"
                  class="!text-[10px]"
                />
                <span v-if="!Object.keys(data.leadStages || {}).length" class="text-gray-300 text-xs">—</span>
              </div>
            </template>
          </Column>
        </DataTable>
      </div>
    </template>

    <!-- Cohort retention -->
    <template v-if="cohorts">
      <div class="bg-white border border-gray-200 rounded-2xl p-4 md:p-5 overflow-x-auto shadow-sm">
        <h2 class="text-sm font-semibold text-gray-900 mb-1">שימור קוהורטות — אחוז העסקים עם תורים בחודשים שאחרי ההרשמה</h2>
        <p class="text-xs text-gray-400 mb-4">~ מציין חודש שעדיין לא הסתיים</p>
        <table class="w-full text-sm border-separate" style="border-spacing: 4px">
          <thead>
            <tr class="text-xs text-gray-400">
              <th class="text-right font-medium px-2 py-1">חודש הרשמה</th>
              <th class="font-medium px-2 py-1">עסקים</th>
              <th class="font-medium px-2 py-1">פעילים בחודש ההרשמה</th>
              <th v-for="o in cohorts.maxOffset" :key="o" class="font-medium px-2 py-1">חודש +{{ o }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c of cohorts.cohorts" :key="c.month">
              <td class="text-right px-2 py-2 font-medium text-gray-900 whitespace-nowrap">{{ formatMonth(c.month) }}</td>
              <td class="text-center px-2 py-2 text-gray-600">{{ c.size }}</td>
              <td class="text-center px-2 py-2 rounded-lg" :class="retentionCellClass(c.size ? Math.round((c.activeInMonth0 / c.size) * 100) : 0)">
                <template v-if="c.size">{{ Math.round((c.activeInMonth0 / c.size) * 100) }}%</template>
                <template v-else>—</template>
              </td>
              <td v-for="o in cohorts.maxOffset" :key="o" class="text-center px-2 py-2 rounded-lg" :class="retentionCellAt(c, o) !== null ? retentionCellClass(retentionCellAt(c, o)!.pct) : 'text-gray-300'">
                <template v-if="retentionCellAt(c, o)">
                  {{ retentionCellAt(c, o)!.pct }}%<span v-if="retentionCellAt(c, o)!.partial">~</span>
                </template>
                <template v-else>—</template>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!cohorts.cohorts.some((c) => c.size > 0)" class="text-center text-gray-400 text-sm py-6">
          אין הרשמות בתקופה שנבחרה
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { DateTime } from 'luxon';
import type {
  IPlatformCohort,
  IPlatformCohortRetention,
  IPlatformCohortsResponse,
  IPlatformFunnelResponse,
} from '@torim-app/types';
import { PlatformLeadStageEnum } from '@torim-app/types';
import { AnalyticsApi } from '../api/analytics-api';
import KpiCard from '../components/KpiCard.vue';
import { useNotify } from '../composables/useNotify';
import { STAGE_LABELS, STAGE_SEVERITY, formatCurrency } from '../composables/useLeadDisplay';

const { notifyError } = useNotify();
const loading = ref(false);
const funnel = ref<IPlatformFunnelResponse | null>(null);
const cohorts = ref<IPlatformCohortsResponse | null>(null);
const selectedMonths = ref(6);

const monthsOptions = [
  { label: '3 חודשים', value: 3 },
  { label: '6 חודשים', value: 6 },
  { label: '12 חודשים', value: 12 },
];

const formatMonth = (m: string) => DateTime.fromFormat(m, 'yyyy-MM').setLocale('he').toFormat('LLLL yyyy');

const stageLabel = (stage: string) => STAGE_LABELS[stage as PlatformLeadStageEnum] || stage;
const stageSeverity = (stage: string) => STAGE_SEVERITY[stage as PlatformLeadStageEnum] || 'secondary';

const retentionCellAt = (c: IPlatformCohort, offset: number): IPlatformCohortRetention | null =>
  c.retention.find((r) => r.offset === offset) || null;

const retentionCellClass = (pct: number) => {
  if (pct >= 60) return 'bg-emerald-100 text-emerald-800 font-semibold';
  if (pct >= 40) return 'bg-emerald-50 text-emerald-700 font-medium';
  if (pct >= 20) return 'bg-amber-50 text-amber-700';
  if (pct > 0) return 'bg-red-50 text-red-600';
  return 'bg-gray-50 text-gray-400';
};

const funnelChartData = computed(() => {
  const months = funnel.value?.months || [];
  const labels = months.map((m) => formatMonth(m.month));
  return {
    labels,
    datasets: [
      { label: 'משלמים', data: months.map((m) => m.paying), backgroundColor: '#10b981', stack: 's' },
      { label: 'בניסיון', data: months.map((m) => m.trial), backgroundColor: '#f59e0b', stack: 's' },
      { label: 'חינמי', data: months.map((m) => m.free), backgroundColor: '#9ca3af', stack: 's' },
      { label: 'פג תוקף', data: months.map((m) => m.expired), backgroundColor: '#ef4444', stack: 's' },
    ],
  };
});

const funnelChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom', labels: { font: { size: 11 }, color: '#6b7280' } },
  },
  scales: {
    y: {
      stacked: true,
      beginAtZero: true,
      ticks: { color: '#9ca3af', precision: 0 },
      grid: { color: 'rgba(0, 0, 0, 0.05)' },
    },
    x: {
      stacked: true,
      ticks: { font: { size: 10 }, color: '#9ca3af' },
      grid: { display: false },
    },
  },
};

const fetchData = async () => {
  loading.value = true;
  try {
    const [funnelRes, cohortsRes] = await Promise.all([
      AnalyticsApi.fetchFunnel(selectedMonths.value),
      AnalyticsApi.fetchCohorts(selectedMonths.value),
    ]);
    funnel.value = funnelRes;
    cohorts.value = cohortsRes;
  } catch (e) {
    console.error('Failed to fetch analytics:', e);
    notifyError('טעינת האנליטיקות נכשלה');
  } finally {
    loading.value = false;
  }
};

watch(selectedMonths, fetchData);
onMounted(fetchData);
</script>
